export const BILLING_ALERT_PRODUCTS = ['event_gateway', 'outpost'] as const;

export type BillingAlertProduct = (typeof BILLING_ALERT_PRODUCTS)[number];

export const BILLING_ALERT_PRODUCT_NAMES: Record<BillingAlertProduct, string> = {
  event_gateway: 'Event Gateway',
  outpost: 'Outpost',
};

export const isBillingAlertProduct = (value: unknown): value is BillingAlertProduct =>
  BILLING_ALERT_PRODUCTS.includes(value as BillingAlertProduct);

// Thresholds are in the billing account's currency, in major units (100.25 = $100.25). Any
// positive amount within the currency's precision is valid, so the minimum is one minor unit.
export const BILLING_ALERT_MAX_THRESHOLD = 999_999_999.99;

const currencyFormatter = (currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency });

/** Decimal places of the currency's minor unit: 2 for USD, 0 for JPY. */
export const getCurrencyDecimals = (currency: string) =>
  currencyFormatter(currency).resolvedOptions().maximumFractionDigits ?? 2;

export const getCurrencySymbol = (currency: string) =>
  currencyFormatter(currency)
    .formatToParts(0)
    .find((part) => part.type === 'currency')?.value ?? currency;

export const formatCurrencyAmount = (amount: number, currency: string) =>
  currencyFormatter(currency).format(amount);

/** Whether the amount uses no more decimals than the currency's minor unit allows. */
export const hasCurrencyPrecision = (amount: number, currency: string) =>
  Number(amount.toFixed(getCurrencyDecimals(currency))) === amount;

export interface BillingAlert {
  product: BillingAlertProduct;
  /** ISO 4217 code of the product subscription's billing currency. */
  currency: string;
  threshold: number | null;
  enabled: boolean;
}
